<script setup lang="ts">
import type { ContentFile } from '~/models/Content/ContentFile'
import type { FederatedModuleMount } from '~/models/Brikke/FederatedModuleMount'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { ContentType } from '~/models/Content/ContentType'
import useContentApi from '~/api/contentApi'
import { getAccessToken } from '~/api/client/oidcClient'

const { code, libraries, fileContentIds } = defineProps<{
  code: string
  libraries: string[]
  fileContentIds?: number[]
}>()

const editorContainerRef = ref<HTMLElement | null>(null)
const brikkeModule = ref<FederatedModuleMount | null>(null)

const { findContents } = useContentApi()

const files = useQuery({
  queryKey: computed(() => ['files', fileContentIds]),
  enabled: computed(() => !!fileContentIds?.length),
  queryFn: () => findContents<ContentFile>({
    contentIdCriterion: fileContentIds,
    contentTypeCriterion: [ContentType.File],
  }, fileContentIds!.length),
  select: data => data.map(({ contentId, file }) => ({
    name: file?.fileName,
    url: `${import.meta.env.VITE_CONTENT_API_URL}/download/${contentId}/file`
  })),
})

let cleanup: (() => void) | null = null

const updateEditor = () => {
  if (!editorContainerRef.value || !brikkeModule.value)
    return

  // Clean up previous instance if it exists
  if (cleanup)
    cleanup()

  const el = editorContainerRef.value

  cleanup = brikkeModule.value.default.mount(el, {
    component: 'CodeEditor',
    props: {
      code,
      libraries,
      files: files.data.value,
      getAuthToken: () => getAccessToken(),
    },
  })
}

// Watch for changes to files data and update the editor when it changes
watch(files.data, (newFiles) => {
  if (newFiles && brikkeModule.value) {
    updateEditor()
  }
}, { deep: true })

onBeforeUnmount(() => {
  if (cleanup)
    cleanup()
})

onMounted(() => {
  // @ts-expect-error we are using a dynamic import here
  import('brikke/brikke').then((module) => {
    brikkeModule.value = module
    updateEditor()
  })
})
</script>

<template>
  <div ref="editorContainerRef" />
</template>
